'use client';

import { type FC } from 'react';
import type { Team } from '@cs-shot/battle-sim';
import { Avatar } from '../../ui';
import { sideColor } from './sideColor';
import { rushMidAvatarUrl } from './avatarUrl';
import { segments, type OpenSeat } from './OpenGameScreen';
import { useStoredImageSrc } from '../../../lib/useStoredAsset';
import { bustCache } from '../render/battleAssetVersion';

// Uniform adaptive slots: every segment gets flex-grow/shrink proportional to its cell count
// and a per-cell max-width cap (1.75rem). So each avatar caps at 28px and every slot — solo,
// free, or inside a party frame — shrinks to the SAME size when the row is tight, and a full
// 5v5 (10 circles) fits any card width without overflow or clipping. Party frames survive.
const CELL_REM = 1.75;
const segStyle = (cells: number) => ({ flexGrow: cells, flexShrink: cells, flexBasis: 0, maxWidth: `${cells * CELL_REM}rem` });

const MiniAvatar: FC<{ seat: OpenSeat }> = ({ seat }) => (
  <div className="aspect-square min-w-0 flex-1">
    <Avatar size="s" className="size-full" src={seat.bot ? undefined : rushMidAvatarUrl(seat.userId)} fallbackKey={seat.userId} firstName={seat.name} />
  </div>
);

/** A side's compact avatar strip on a card — party seats share a bordered frame, free slots
 *  render as dashed dots. Anonymous: only the dense partyGroup index is shown, never a code. */
export const MiniSide: FC<{ side: Team; seats: readonly (OpenSeat | null)[] }> = ({ side, seats }) => (
  <div className="flex min-w-0 flex-1 items-center justify-center gap-1">
    {segments(seats).map((seg, idx) => {
      if (seg.kind === 'party') {
        return (
          <div key={`p${idx}`} style={segStyle(seg.seats.length)} className={`flex min-w-0 items-center gap-0.5 rounded-lg border p-0.5 ${sideColor(side, 'border')}`}>
            {seg.seats.map((seat) => <MiniAvatar key={seat.userId} seat={seat} />)}
          </div>
        );
      }
      if (seg.kind === 'solo') {
        return (
          <div key={`s${idx}`} style={segStyle(1)} className="aspect-square min-w-0">
            <Avatar size="s" className="size-full" src={seg.seat.bot ? undefined : rushMidAvatarUrl(seg.seat.userId)} fallbackKey={seg.seat.userId || `bot${idx}`} firstName={seg.seat.name} />
          </div>
        );
      }
      return <div key={`f${seg.index}`} style={segStyle(1)} className="aspect-square min-w-0 rounded-full border-2 border-solid border-white/20" />;
    })}
  </div>
);

/** The rolled arena's cover as the card backdrop (via the asset store — a TMA webview
 *  re-downloads plain URLs every session) under a darkening gradient that keeps the
 *  text/avatars readable. */
export const CardBackdrop: FC<{ map: string }> = ({ map }) => {
  const src = useStoredImageSrc(bustCache(`/battle/cover_${map}.webp`));
  return (
    <div className="pointer-events-none absolute inset-0" aria-hidden>
      {src && <img src={src} alt="" className="h-full w-full object-cover" />}
      <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/35 to-black/55" />
    </div>
  );
};
